import React, { Component } from 'react';

import loading_animation from 'animations/tqps-loading.gif';
import link_button from 'icons/link-white.png';

const MAX_VISIBLE_BUTTONS = 4;
const SHRINK_TAG_MIN_FONT_SIZE = 9;

// Wraps a tag/category button and shrinks its font size (down to a floor) until
// the text fits within its 2-line clamped box, instead of getting cut off with
// an ellipsis. Falls back to the existing line-clamp truncation at the floor.
class ShrinkFitTag extends Component {
	constructor(props) {
		super(props);
		this.ref = React.createRef();
	}

	componentDidMount() {
		this.fit();
		window.addEventListener('resize', this.fit);
	}

	componentDidUpdate(prevProps) {
		if (prevProps.children !== this.props.children) {
			this.fit();
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.fit);
	}

	fit = () => {
		const el = this.ref.current;
		if (!el) return;

		el.style.fontSize = '';
		let fontSize = parseFloat(window.getComputedStyle(el).fontSize);

		let guard = 0;
		while (el.scrollHeight > el.clientHeight + 1 && fontSize > SHRINK_TAG_MIN_FONT_SIZE && guard < 20) {
			fontSize -= 0.5;
			el.style.fontSize = `${fontSize}px`;
			guard += 1;
		}
	};

	render() {
		const { className, onClick, children } = this.props;
		return (
			<div ref={this.ref} className={className} onClick={onClick}>
				{children}
			</div>
		);
	}
}

class Resource extends Component {
	constructor(props) {
		super(props);
		this.state = { expanded: false, copied: false };
	}

	copyShareLink = () => {
		const url = `${window.location.origin}/r/${this.props.data.id}`;
		navigator.clipboard.writeText(url);
		this.setState({ copied: true });
		setTimeout(() => this.setState({ copied: false }), 2000);
	};

	render() {
		const data = this.props.data;
		const selected_tags = this.props.selected_tags || [];

		// Surface any selected tag that would otherwise be hidden behind the cap —
		// recomputed every render, so it re-sorts whenever the filters change.
		const sorted_tags = [...data.tags].sort((a, b) => {
			const a_selected = selected_tags.includes(a) ? 0 : 1;
			const b_selected = selected_tags.includes(b) ? 0 : 1;
			return a_selected - b_selected;
		});

		const total_buttons = 1 + sorted_tags.length; // +1 for the category button
		const has_overflow = total_buttons > MAX_VISIBLE_BUTTONS;
		const visible_tags =
			this.state.expanded || !has_overflow
				? sorted_tags
				: sorted_tags.slice(0, MAX_VISIBLE_BUTTONS - 1);

		let resource_tags = visible_tags.map(tag => {
			const active = selected_tags.includes(tag);
			return (
				<ShrinkFitTag
					key={tag}
					onClick={() => this.props.select_tag(tag)}
					className={`resource-tag ${active ? 'resource-tag--selected' : ''}`}
				>
					{tag}
				</ShrinkFitTag>
			);
		});

		return (
			<div className="resource-container">
				<div
					className="tooltip-wrapper"
					data-tooltip={this.props.data.blurb}
					style={{ float: 'right' }}
				>
					<img
						onClick={() => window.open(this.props.data.download_url, '_blank')}
						className="resource-thumbnail"
						src={data.thumbnail}
					/>
				</div>
				<p className="resource-title">{data.title}</p>
				<div className="resource-tags-container">
					<CategoryTag
						name={this.props.data.category}
						select_category={() => this.props.select_category(this.props.data.category)}
					/>
					{resource_tags}
					{has_overflow && (
						<button
							className="resource-tags-see-more"
							onClick={() => this.setState({ expanded: !this.state.expanded })}
						>
							{this.state.expanded ? '...see less' : '...see more'}
						</button>
					)}
				</div>
				<div className="resource-card-footer">
					<div className={`resource-badge-${data.public ? 'public' : 'private'}`}>
						{`${data.public ? 'Public' : 'Private'}`} Resource
					</div>
					{data.id && (
						<button
							className={`resource-share-btn ${this.state.copied ? 'resource-share-btn--copied' : ''}`}
							onClick={this.copyShareLink}
							title="Copy a permanent share link"
						>
							{this.state.copied ? (
								'Link copied!'
							) : (
								<>
									<img src={link_button} alt="" className="resource-share-icon" />
									Share
								</>
							)}
						</button>
					)}
				</div>
			</div>
		);
	}
}

class ResourceListItem extends Component {
	constructor(props) {
		super(props);
		this.state = { previewBelow: false, previewActive: false, copied: false };
		this.thumbRef = React.createRef();
	}

	copyShareLink = e => {
		e.stopPropagation();
		const url = `${window.location.origin}/r/${this.props.data.id}`;
		navigator.clipboard.writeText(url);
		this.setState({ copied: true });
		setTimeout(() => this.setState({ copied: false }), 2000);
	};

	handleThumbEnter = () => {
		if (this.thumbRef.current) {
			const thumbRect = this.thumbRef.current.getBoundingClientRect();
			const container = this.thumbRef.current.closest('.resource-window');
			const containerTop = container ? container.getBoundingClientRect().top : 0;
			const distanceFromContainerTop = thumbRect.top - containerTop;
			this.setState({ previewBelow: distanceFromContainerTop < 300, previewActive: true });
		}
	};

	handleThumbLeave = () => {
		this.setState({ previewActive: false });
	};

	render() {
		const { data, select_category, select_tag } = this.props;
		const { previewBelow, previewActive } = this.state;
		return (
			<div
				className={`resource-list-item ${previewActive ? 'resource-list-item--preview-active' : ''}`}
				data-tooltip={data.blurb}
				onClick={() => window.open(data.download_url, '_blank')}
			>
				<div
					className="resource-list-thumb-wrap"
					ref={this.thumbRef}
					onMouseEnter={this.handleThumbEnter}
					onMouseLeave={this.handleThumbLeave}
				>
					<img className="resource-list-thumb" src={data.thumbnail} alt="" />
					<img
						className={`resource-list-thumb-preview ${previewBelow ? 'resource-list-thumb-preview--below' : ''}`}
						src={data.thumbnail}
						alt=""
					/>
				</div>
				<span className="resource-list-title">{data.title}</span>
				<span
					className="resource-list-category"
					onClick={e => {
						e.stopPropagation();
						select_category(data.category);
					}}
				>
					{data.category}
				</span>
				<div className="resource-list-desc-wrap">
					<span className="resource-list-desc">{data.blurb}</span>
				</div>
				<div className="resource-list-tags">
					{data.tags.map(tag => (
						<span
							key={tag}
							className={`resource-list-tag ${this.props.selected_tags && this.props.selected_tags.includes(tag) ? 'resource-list-tag--selected' : ''}`}
							onClick={e => {
								e.stopPropagation();
								select_tag(tag);
							}}
						>
							{tag}
						</span>
					))}
				</div>
				<span className="resource-list-access">
					<span
						className={`resource-list-badge resource-list-badge--${data.public ? 'public' : 'private'}`}
					>
						{data.public ? 'Public' : 'Private'}
					</span>
					{data.id && (
						<button
							className={`resource-list-share ${this.state.copied ? 'resource-list-share--copied' : ''}`}
							onClick={this.copyShareLink}
							title="Copy a permanent share link"
						>
							{this.state.copied ? (
								'✓'
							) : (
								<img src={link_button} alt="" className="resource-share-icon" />
							)}
						</button>
					)}
				</span>
			</div>
		);
	}
}

class CategoryTag extends Component {
	render() {
		return (
			<ShrinkFitTag className="category-tag" onClick={this.props.select_category}>
				{this.props.name}
			</ShrinkFitTag>
		);
	}
}

const GridIcon = ({ active }) => (
	<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect x="1" y="1" width="6" height="6" rx="1.5" fill={active ? 'white' : 'rgba(255,255,255,0.4)'} />
		<rect x="11" y="1" width="6" height="6" rx="1.5" fill={active ? 'white' : 'rgba(255,255,255,0.4)'} />
		<rect x="1" y="11" width="6" height="6" rx="1.5" fill={active ? 'white' : 'rgba(255,255,255,0.4)'} />
		<rect x="11" y="11" width="6" height="6" rx="1.5" fill={active ? 'white' : 'rgba(255,255,255,0.4)'} />
	</svg>
);

const ListIcon = ({ active }) => (
	<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect
			x="1"
			y="2"
			width="16"
			height="2.5"
			rx="1.25"
			fill={active ? 'white' : 'rgba(255,255,255,0.4)'}
		/>
		<rect
			x="1"
			y="7.75"
			width="16"
			height="2.5"
			rx="1.25"
			fill={active ? 'white' : 'rgba(255,255,255,0.4)'}
		/>
		<rect
			x="1"
			y="13.5"
			width="16"
			height="2.5"
			rx="1.25"
			fill={active ? 'white' : 'rgba(255,255,255,0.4)'}
		/>
	</svg>
);

export default class ResourceWindow extends Component {
	constructor(props) {
		super(props);
		this.state = { view: 'grid' };
	}

	render() {
		const { view } = this.state;

		let user_logged_in_badge = '';
		if (this.props.user) {
			user_logged_in_badge = (
				<div className="user-logged-in-badge">
					Logged In:{' '}
					<div style={{ fontWeight: 'bold', display: 'inline-block' }}>{this.props.user.name}</div>
				</div>
			);
		}

		// Rendered as a child of a `position: sticky` header — in list view that's
		// the real .resource-list-header row; in grid view an invisible twin of
		// the same height — so the controls scroll/stick in lockstep with it
		// instead of drifting via a separately-positioned overlay.
		const headerControls = (
			<div className="resource-header-controls">
				{user_logged_in_badge}
				<div className="view-toggle">
					<button
						className={`view-toggle-btn ${view === 'grid' ? 'active' : ''}`}
						onClick={() => this.setState({ view: 'grid' })}
						title="Grid view"
					>
						<GridIcon active={view === 'grid'} />
					</button>
					<button
						className={`view-toggle-btn ${view === 'list' ? 'active' : ''}`}
						onClick={() => this.setState({ view: 'list' })}
						title="List view"
					>
						<ListIcon active={view === 'list'} />
					</button>
				</div>
			</div>
		);

		let resources;
		if (this.props.loading) {
			resources = <img className="loading-animation" src={loading_animation} />;
		} else if (view === 'grid') {
			resources = (
				<div className="resource-grid">
					<div className="resource-grid-header">{headerControls}</div>
					<div className="resource-grid-cards">
						{this.props.resources.map(r => (
							<Resource
								key={r.title}
								data={r}
								select_category={this.props.select_category}
								select_tag={this.props.select_tag}
								selected_tags={this.props.selected_tags}
							/>
						))}
					</div>
				</div>
			);
		} else {
			resources = (
				<div className="resource-list">
					<div className="resource-list-header">
						<span></span>
						<span>Title</span>
						<span>Category</span>
						<span>Description</span>
						<span>Tags</span>
						<span></span>
						{headerControls}
					</div>
					{this.props.resources.map(r => (
						<ResourceListItem
							key={r.title}
							data={r}
							select_category={this.props.select_category}
							select_tag={this.props.select_tag}
							selected_tags={this.props.selected_tags}
						/>
					))}
				</div>
			);
		}

		return (
			<div className="resource-layout">
				<div className={`resource-window ${view === 'list' ? 'resource-window--list' : ''}`}>
					{resources}
				</div>
			</div>
		);
	}
}
